import ReactGA from 'react-ga4';
import { eventsGA } from './types';

let currentUserId: string | null = null;

export const initGA = () => {
    if (process.env.REACT_APP_GA_ID) {
        ReactGA.initialize(process.env.REACT_APP_GA_ID as string, {
            testMode: process.env.NODE_ENV === 'development'
        });
    } else {
        console.warn('GA_ID not found in environment variables');
    }
}

export const setUserToGA = (userId: string) => {
    if (!userId) {
        console.warn('Attempted to set empty user ID to GA');
        return;
    }
    
    currentUserId = userId;
    ReactGA.set({ user_id: userId });
}

export const clearUserFromGA = () => {
    currentUserId = null;
    ReactGA.set({ user_id: null });
}

export const trackEventFromGA = (
    eventKey: keyof typeof eventsGA,
    options?: Record<string, any>
) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    
    const eventValue = eventsGA[eventKey];
    const eventData = {
        action: typeof eventValue === 'string' ? eventValue : String(eventValue),
        category: options?.category || 'general',
        ...(options || {}),
        ...(currentUserId && { user_id: currentUserId })
    };
    
    ReactGA.event(eventData);
}

export const trackGAEvent = (
    event: string | Record<string, any>, 
    options?: string | Record<string, any>
) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    
    const optionsObject = typeof options === 'string' 
        ? { category: options } 
        : (options || {});
    
    if (typeof event === 'string') {
        const eventObject = {
            action: event,
            category: optionsObject.category || 'general', 
            ...optionsObject,
            ...(currentUserId && { user_id: currentUserId })
        };
        
        ReactGA.event(eventObject);
    } else {
        const eventName = (event as any).action || 'custom_event';
        
        const { action, ...eventParamsWithoutAction } = event as any;
        const eventParams = {
            action: eventName,
            category: event.category || optionsObject.category || 'general', 
            ...eventParamsWithoutAction,
            ...optionsObject,
            ...(currentUserId && { user_id: currentUserId })
        };
        
        ReactGA.event(eventParams);
    }
}

export const sendPageViewGA = (id?: string) => {
    const userId = id || currentUserId;
    
    if (userId) {
        ReactGA.set({ user_id: userId });
        if (id && id !== currentUserId) {
            currentUserId = id;
        }
    }
    
    const pageViewData = {
        hitType: "pageview" as const,
        page: window.location.pathname + window.location.search,
        title: document.title || "Page View",
        ...(userId && { user_id: userId })
    };
    
    ReactGA.send(pageViewData);
}

export const getCurrentUserId = (): string | null => {
    return currentUserId;
}

export const trackPageView = (pagePath?: string, pageTitle?: string) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    
    ReactGA.send({
        hitType: "pageview",
        page: pagePath || (window.location.pathname + window.location.search),
        title: pageTitle || document.title || "Page View",
        ...(currentUserId && { user_id: currentUserId })
    });
}

export const ensureUserContext = (callback: () => void) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    callback();
}

export const trackGA4Event = (
    eventName: string,
    eventParams?: Record<string, any>
) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    
    ReactGA.event(eventName, {
        ...eventParams,
        ...(currentUserId && { user_id: currentUserId })
    });
}

export const trackEventWithContext = (
    eventName: string,
    eventParams?: Record<string, any>
) => {
    if (currentUserId) {
        ReactGA.set({ user_id: currentUserId });
    }
    
    const eventData = {
        action: eventName,
        category: eventParams?.category || 'general', 
        ...(eventParams || {}),
        ...(currentUserId && { user_id: currentUserId })
    };
    
    ReactGA.event(eventData);
}